import axios from 'axios';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import CreditCardForm from '../../../Components/Common/CreditCardForm';
import LogoSection from '../../../Components/Common/LogoSection';
import Spinner from '../../../Components/Common/Spinner';
import Stepper from '../../../Components/Common/Stepper';
import StepperMobile from '../../../Components/Common/StepperMobile';
import { addVerifyingCard, resetVerifyingCardData } from '../../../Reducers/cardReducer';
import { updateOnboardingStep } from '../../../Reducers/signupReducer';
import { AppDispatch, RootState } from '../../../Store';
import {
  logErrorAsync,
  logSuccessAsync,
  saveCardDetails,
  sendAdminAlertAsync,
  updatePaymentStepAsync,
} from '../../../Utils/api';
import {
  BUSINESS,
  INDIVIDUAL,
  Moyassar3dSecureResponse,
  Moyassar3dSecureStatusResponse,
  MoyassarErrorMessages,
  ONBOARDING_STEPS,
  RestrictedCardTypes,
} from '../../../Utils/constants';
import { isExpired } from '../../../Utils/helperFunctions';
import CardComponent from '../../ManagePayment/CardComponent';
import ButtonsSection from '../Common/ButtonsSection';
import CardOptions from './CardOptions';

const PaymentInformation = () => {
  const [cardData, setCardData] = useState({
    name: '',
    number: '',
    cvc: '',
    month: '',
    year: '',
  });

  const [paymentDetails, setPaymentDetails] = useState({
    id: '',
    status: '',
    last4: '',
    type: '',
    expiryDate: '',
  });

  const [error, setError] = useState('');
  const [errorDescription, setErrorDescription] = useState('');
  const [currentUrl, setCurrentUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [verificationUrl, setVerificationUrl] = useState('');
  const [cardApproved, setCardApproved] = useState(false);
  const verifyingCard = useSelector((state: RootState) => state.cards?.verifyingCard);
  const paymentMethod = useSelector(
    (state: RootState) => state.signup?.customer?.customer?.paymentMethod,
  );

  const card = useSelector((state: RootState) => state.cards?.cards[0]);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const params = new URLSearchParams(location.search);
  const messages = params.getAll('message');
  const message = messages.length > 0 ? messages[messages.length - 1] : null;
  const statuses = params.getAll('status');
  const status = statuses.length > 0 ? statuses[statuses.length - 1] : null;
  const ids = params.getAll('id');
  const paymentId = ids.length > 0 ? ids[ids.length - 1] : '';

  const sendCriticalPaymentAlert = () => {
    const adminAlert = {
      title: 'CRITICAL: Payment verification system failure - no response data',
      alert_details: {
        issue: 'Payment verification response missing both message and status',
        paymentId: paymentId || 'N/A',
        rawMessage: message,
        rawStatus: status,
        urlParams: {
          message: new URLSearchParams(window.location.search).getAll('message'),
          status: new URLSearchParams(window.location.search).getAll('status'),
          id: new URLSearchParams(window.location.search).getAll('id'),
        },
        timestamp: new Date().toISOString(),
        severity: 'CRITICAL',
        requiresImmediateAction: true,
        possibleCauses: [
          'Moyasar API response format changed',
          'Network issues during payment processing',
          'Payment gateway integration issue',
          'Unexpected error in payment flow',
        ],
      },
    };
    dispatch(sendAdminAlertAsync(adminAlert));
  };

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleResponse = () => {
      // Only process if we have URL parameters (indicating a payment verification response)
      if (!paymentId) {
        return; // No payment verification data, skip processing
      }

      if (message === Moyassar3dSecureResponse.APPROVED) {
        setCardApproved(true);
        localStorage.removeItem('cardData');

        // Success log for APPROVED message
        const successLog = {
          success_message: 'Card verification completed successfully - APPROVED',
          success_details: {
            paymentId: paymentId || '',
            message: message || '',
            status: status || '',
            action: 'CARD_VERIFICATION_APPROVED',
          },
        };
        dispatch(logSuccessAsync(successLog));
      } else if (message && message in MoyassarErrorMessages) {
        setError(MoyassarErrorMessages[message]);
        if (message === Moyassar3dSecureResponse.DECLINED_INVALID_PIN) {
          const savedCardData = localStorage.getItem('cardData');
          if (savedCardData) {
            setCardData(JSON.parse(savedCardData));
          }
        }
        dispatch(resetVerifyingCardData());
        localStorage.removeItem('cardData');

        // Error log for failed verification with specific message
        const errorLog = {
          error_message: `Card verification failed: ${MoyassarErrorMessages[message]}`,
          error_details: {
            paymentId: paymentId || '',
            message: message || '',
            status: status || '',
            action: 'CARD_VERIFICATION_FAILED',
            errorType: 'MOYASAR_ERROR',
          },
        };
        dispatch(logErrorAsync(errorLog));
      } else if (status === Moyassar3dSecureStatusResponse.PAID) {
        setCardApproved(true);
        localStorage.removeItem('cardData');

        // Success log for PAID status
        const successLog = {
          success_message: 'Payment verification completed successfully - PAID',
          success_details: {
            paymentId: paymentId || '',
            message: message || '',
            status: status || '',
            action: 'PAYMENT_STATUS_PAID',
          },
        };
        dispatch(logSuccessAsync(successLog));
      } else if (status === Moyassar3dSecureStatusResponse.FAILED) {
        setError('Something went wrong, please try again');
        dispatch(resetVerifyingCardData());
        localStorage.removeItem('cardData');

        // Error log for FAILED status
        const errorLog = {
          error_message: 'Payment verification failed - status FAILED',
          error_details: {
            paymentId: paymentId || '',
            message: message || '',
            status: status || '',
            action: 'PAYMENT_STATUS_FAILED',
            errorType: 'STATUS_FAILED',
          },
        };
        dispatch(logErrorAsync(errorLog));
      } else {
        // CRITICAL: Fallback for when neither message nor status is available
        setError('Payment verification failed. Please try again or contact support.');
        dispatch(resetVerifyingCardData());
        localStorage.removeItem('cardData');

        // Error log for unknown/fallback error
        const errorLog = {
          error_message:
            'CRITICAL: Payment verification failed - neither message nor status present',
          error_details: {
            paymentId: paymentId || '',
            message: message || '',
            status: status || '',
            action: 'PAYMENT_VERIFICATION_CRITICAL_ERROR',
            errorType: 'CRITICAL_FALLBACK_ERROR',
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            currentUrl: window.location.href,
          },
        };
        dispatch(logErrorAsync(errorLog));

        // Send critical admin alert for immediate attention
        sendCriticalPaymentAlert();
      }
      setCurrentUrl(window.location.href);
    };
    handleResponse();
  }, [message, dispatch, setCurrentUrl]);

  useEffect(() => {
    if (verifyingCard) {
      const { status, expiryDate, paymentIdentifier, last4, type, cardHolderName } = verifyingCard;

      if (cardApproved && paymentIdentifier.length > 0) {
        dispatch(resetVerifyingCardData());
        dispatch(
          saveCardDetails({
            paymentId: paymentId,
            id: paymentIdentifier,
            last4: last4,
            expiryDate: expiryDate,
            status: status,
            type: type,
            navigate: navigate,
            setIsLoading: setIsLoading,
            setErrorMessage: setError,
            setErrorDescription,
            isAdmin: false,
            customerId: undefined,
            isOnboarding: true,
            cardHolderName,
          }),
        );
      }
    }
  }, [cardApproved]);

  useEffect(() => {
    setPaymentDetails((prevState) => ({
      ...prevState,
      id: (card?.id || paymentMethod?.id?.toLocaleString()) ?? '',
      status: (card?.status || paymentMethod?.status) ?? '',
      last4: (card?.last4 || paymentMethod?.last4) ?? '',
      type: (card?.type || paymentMethod?.type) ?? '',
      expiryDate: card?.expiryDate || moment(paymentMethod?.expiryDate).format('MM/YY') || '',
    }));
    setCurrentUrl(window.location.href);
  }, []);

  useEffect(() => {
    if (verificationUrl) {
      //direct to 3d secure url
      window.open(verificationUrl, '_self');
    }
  }, [verificationUrl]);

  const accountType = useSelector((state: RootState) => state.signup.accountType);

  const handleBack = () => {
    navigate('/signup');

    if (accountType === BUSINESS) {
      dispatch(updateOnboardingStep(ONBOARDING_STEPS.BILLING_EMAIL));
    } else {
      dispatch(updateOnboardingStep(ONBOARDING_STEPS.SUPPORT_PACKAGE));
    }
  };

  const handleNext = () => {
    if (paymentDetails.last4.length === 4) {
      navigate('/signup');

      dispatch(updatePaymentStepAsync(setIsLoading, setError, navigate));
    } else {
      handleSave();
    }
  };

  const handleSave = () => {
    const currentDate = new Date();
    const currentYear: string = currentDate.getFullYear().toString().slice(-2);
    const currentMonth: number = currentDate.getMonth() + 1;
    if (cardData.number.length !== 16 && cardData.number.length !== 15)
      setError('Invalid card number');
    else if (cardData.cvc.length !== 3) setError('Invalid CVV');
    else if (!cardData.name.includes(' ')) setError('Invalid Card Holder Name');
    else if (Number(cardData.month) > 12) setError('Invalid month');
    else if (Number(cardData.year) < Number(currentYear)) setError('Invalid year');
    else if (Number(cardData.year) === Number(currentYear) && Number(cardData.month) < currentMonth)
      setError('Card has expired');
    else {
      setIsLoading(true);

      const apiUrl = 'https://api.moyasar.com/v1/tokens';
      const config = {
        auth: {
          username: process.env.REACT_APP_MOYASAR_PUBLIC_KEY ?? '',
          password: '',
        },
        body: {
          ...cardData,
          callback_url: currentUrl,
        },
      };
      axios
        .post(apiUrl, config.body, { auth: config.auth })
        .then((response) => {
          const expiryDate = `${response.data.month}/${response.data.year.slice(-2)}`;
          const cardCredentials = {
            id: response.data?.id,
            status: 'primary',
            paymentIdentifier: response.data?.id,
            last4: response.data?.last_four,
            expiryDate,
            type: response.data?.brand || 'visa',
            cardHolderName: response.data?.name,
          };

          if (response.data.verification_url === null) {
            const errorLog = {
              error_message: response.data.message ?? 'An error occurred during verification.',
              error_details: { ...response.data },
            };
            dispatch(logErrorAsync(errorLog, setIsLoading));
            setError(response.data.message ?? 'An error occurred during verification.');
            return;
          }

          if (cardCredentials.type in RestrictedCardTypes) {
            const errorMessage = `${
              RestrictedCardTypes[cardCredentials.type as keyof typeof RestrictedCardTypes]
            } cards are not accepted, please add another card!.`;

            const errorLog = {
              error_message: errorMessage,
              error_details: { ...response?.data },
            };
            dispatch(logErrorAsync(errorLog, setIsLoading));

            setError(errorMessage);
            return;
          }
          localStorage.setItem('cardData', JSON.stringify(cardData));
          dispatch(addVerifyingCard(cardCredentials));

          // Success log for successful card verification API call
          const successLog = {
            success_message: 'Card verification initiated successfully',
            success_details: {
              cardId: response.data?.id,
              last4: response.data?.last_four,
              brand: response.data?.brand,
              action: 'CARD_VERIFICATION_INITIATED',
              hasVerificationUrl: !!response.data.verification_url,
            },
          };
          dispatch(logSuccessAsync(successLog));

          setVerificationUrl(response.data.verification_url);
        })
        .catch((error) => {
          let errorMessage = error?.response?.data?.message || 'Invalid card details';
          errorMessage = errorMessage.includes(':')
            ? errorMessage.split(':')[1].trim()
            : errorMessage;
          setError(errorMessage);
          setIsLoading(false);
        });
    }
  };
  return (
    <div className="w-full h-full flex items-stretch lg:items-center justify-center">
      {isLoading && <Spinner />}
      {!isLoading && (
        <div className="border-[1px] border-gray-3 rounded-lg py-4 px-8 bg-white-0 min-h-[680px] w-[98%] overflow-auto flex flex-col justify-between">
          <LogoSection />
          {accountType === BUSINESS ? (
            <Stepper type={BUSINESS} currentStep={5} />
          ) : (
            <Stepper type={INDIVIDUAL} currentStep={3} />
          )}
          <StepperMobile
            stepNumber={accountType === BUSINESS ? 5 : 3}
            stepName="Payment Information"
            nextStep=""
            totalSteps={accountType === BUSINESS ? 5 : 3}
          />
          <div className="flex gap-2 items-center my-5">
            <h1 className="text-gray-1 text-lg poppins-500 whitespace-nowrap">
              Payment Information
            </h1>
            <div className="w-full h-[1px] bg-gray-2"></div>
          </div>
          <div className="flex flex-col md:flex-row justify-between">
            <div className="flex flex-col justify-center items-baseline text-left mb-auto md:w-2/5 w-full">
              {paymentDetails.last4.length === 4 ? (
                <CardComponent
                  key={paymentDetails.id}
                  id={paymentDetails.id}
                  title={paymentDetails.status}
                  cardDetails={`${paymentDetails.type || ''} ****-${paymentDetails.last4}`}
                  expiryDate={`Expiry ${paymentDetails.expiryDate}`}
                  dashedBorder={true}
                  setIsLoading={setIsLoading}
                  setError={setError}
                  isExpired={isExpired(paymentDetails.expiryDate)}
                />
              ) : (
                <CreditCardForm
                  cardData={cardData}
                  setCardData={setCardData}
                  isSaveButton={false}
                  setError={setError}
                  error={error}
                />
              )}
            </div>
            <CardOptions isOnBoarding={true} />
          </div>
          <div className="mt-auto">
            <ButtonsSection
              errorMessage={error || ''}
              errorDescription={errorDescription || ''}
              isBackButton={true}
              isNextButton={true}
              nextFunction={handleNext}
              backFunction={handleBack}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentInformation;
